import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TranslocoRootModule } from 'src/app/transloco-root.module';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as Sentry from '@sentry/angular';
import type Hls from 'hls.js';
import type { ErrorData } from 'hls.js';
import {
  SUPPORTED_PLAYBACK_SCHEMA_VERSION,
  VideoPlayback,
} from 'src/app/model/response/video-playback-response';
import { VideoPlaybackService } from 'src/app/service/video-playback.service';

type HlsPlayerUiState = 'processing' | 'ready' | 'failed' | 'unsupported';

/**
 * Player HLS atrás de `environment.HLS_PLAYBACK` (task-front-brief, ruling #3). Dono da sessão
 * inteira: chama `POST /video/{id}/playback-session` via VideoPlaybackService, faz o polling da
 * máquina de estados do contrato, inicializa HLS nativo (Safari) ou hls.js — este último por
 * import dinâmico, só quando realmente precisa — e trata renovação/erros conforme
 * docs/frontend-hls-contract.md.
 *
 * A sessão só começa quando `active` (sinal reaproveitado do card — mesmo gate de
 * `nearViewport`/`maxSlideVisto` que o `<video>` MP4 legado já usa) E este elemento estiverem
 * de fato na viewport (IntersectionObserver próprio) — nunca na montagem do feed
 * (task-front-review.md, bloqueante B1; o contrato do backend proíbe assinar cookies na
 * montagem de listas).
 *
 * `exportAs: 'appHlsVideoPlayer'` + os membros públicos `paused`/`play()`/`pause()` existem só
 * para o card do feed social continuar controlando play/pause por fora (a camada de toque que
 * evita o fullscreen nativo no double-click) sem o card precisar alcançar o `<video>` interno.
 */
@Component({
  selector: 'app-hls-video-player',
  standalone: true,
  exportAs: 'appHlsVideoPlayer',
  imports: [CommonModule, ProgressSpinnerModule, TranslocoRootModule],
  templateUrl: './hls-video-player.component.html',
  styleUrl: './hls-video-player.component.css',
})
export class HlsVideoPlayerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() videoId!: string;
  /**
   * Sinal de ativação vindo do card (review B1): reaproveita exatamente o gate
   * `nearViewport`+`maxSlideVisto` que já existe para o `<video>` MP4 legado (`videoSrc()` no
   * card) — não é um mecanismo novo. Sem isso, todo card do feed dispararia sessão (e cookies
   * CloudFront) na montagem da lista inteira.
   */
  @Input() active = false;

  @ViewChild('video') videoElRef?: ElementRef<HTMLVideoElement>;

  uiState: HlsPlayerUiState = 'processing';
  playback: VideoPlayback | null = null;
  /** True quando o polling desistiu após 180s ainda processando — oferece atualização manual em vez de fabricar FAILED (ver contrato). */
  stalled = false;

  private sessionErrored = false;
  private playerFailure = false;
  private renewAttempted = false;
  private sessionStarted = false;
  private inViewport = false;
  private destroyed = false;
  /**
   * Contador de geração (review R2): incrementado a cada `resetForNewVideo()`. `initPlayer`
   * captura o valor antes do `await import('hls.js')` e confere depois — sem isso, `destroyed`
   * sozinho não pega o caso "vídeo trocou mas o componente continua vivo" (reciclagem futura com
   * `trackBy`): o import em voo do vídeo ANTERIOR resolveria e criaria um `Hls` com o `hls_url`
   * antigo, capturado no closure.
   */
  private playbackGeneration = 0;
  private pollSub?: Subscription;
  private viewportObserver?: IntersectionObserver;
  private hls: Hls | null = null;
  /** Cancela as chamadas avulsas (renovação preventiva/reativa) que `pollSub` não cobre. */
  private readonly destroy$ = new Subject<void>();

  constructor(
    private videoPlaybackService: VideoPlaybackService,
    private host: ElementRef<HTMLElement>
  ) {}

  ngOnInit(): void {
    this.setupViewportObserver();
    this.maybeStartSession();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['videoId'] && !changes['videoId'].firstChange) {
      // Troca de vídeo na MESMA instância (ex.: feed recicla o componente) — destrói o player
      // anterior e recomeça do zero (review I5).
      this.resetForNewVideo();
      return;
    }
    if (changes['active']) {
      this.maybeStartSession();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.viewportObserver?.disconnect();
    this.pollSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
    this.teardownHls();
  }

  // ─── Interop com a camada de toque do card (play/pause por fora) ──────────
  get paused(): boolean {
    return this.videoElRef?.nativeElement.paused ?? true;
  }

  play(): Promise<void> {
    const video = this.videoElRef?.nativeElement;
    return video ? video.play() : Promise.resolve();
  }

  pause(): void {
    this.videoElRef?.nativeElement.pause();
  }

  // ─── UI ─────────────────────────────────────────────────────────────────
  get canRetry(): boolean {
    if (this.sessionErrored || this.playerFailure) {
      return true;
    }
    return !!this.playback?.error?.retryable;
  }

  /** Ação manual: "tentar de novo" (falha) ou "atualizar" (processamento travado após 180s). */
  retry(): void {
    this.sessionErrored = false;
    this.playerFailure = false;
    this.renewAttempted = false;
    this.stalled = false;
    this.teardownHls();
    this.startSession();
  }

  /** Antes de retomar, renova se a autorização estiver perto de expirar (mesma regra do início). */
  onVideoPlayEvent(): void {
    if (!this.playback || !this.isAuthExpiringSoon(this.playback)) {
      return;
    }
    this.videoElRef?.nativeElement.pause();
    this.startPlaybackIfFresh(this.playback);
  }

  /**
   * Evidência REAL de que a reprodução voltou a fluir — não apenas que a API respondeu READY
   * (essa distinção é o bloqueante B2). Libera uma nova renovação automática para uma falha
   * FUTURA e independente; dentro do MESMO incidente (sem `playing` no meio) o guard continua
   * consumido, então uma segunda falha cai direto em ação manual.
   */
  onVideoPlaying(): void {
    this.renewAttempted = false;
  }

  /**
   * HLS nativo (Safari) não expõe o status HTTP no evento `error` do `<video>` — não há como
   * distinguir 401/403/404 aqui (limitação da plataforma, não do código). Mas um erro de
   * decode/codec (`MEDIA_ERR_DECODE`/`MEDIA_ERR_SRC_NOT_SUPPORTED`) nunca se resolve renovando
   * sessão — filtrar para `MEDIA_ERR_NETWORK` evita gastar a renovação única numa causa que ela
   * não corrige, e evita loop numa mídia permanentemente quebrada (review B2).
   */
  onNativeVideoError(): void {
    const code = this.videoElRef?.nativeElement.error?.code;
    this.reportTelemetry('native', undefined, String(code ?? 'unknown'));
    if (code !== MediaError.MEDIA_ERR_NETWORK) {
      this.failPlayback();
      return;
    }
    this.renewOnce();
  }

  // ─── Visibilidade / ativação (review B1) ───────────────────────────────
  private setupViewportObserver(): void {
    if (typeof IntersectionObserver === 'undefined') {
      this.inViewport = true; // navegador sem suporte: mesmo fallback do card, não bloqueia
      this.maybeStartSession();
      return;
    }
    this.viewportObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          this.inViewport = true;
          this.viewportObserver?.disconnect(); // uma vez visto, fica ativo (mesmo padrão do card)
          this.viewportObserver = undefined;
          this.maybeStartSession();
        }
      },
      { rootMargin: '300px 0px' } // mesmo rootMargin do gate nearViewport do card
    );
    this.viewportObserver.observe(this.host.nativeElement);
  }

  /**
   * Só cria a sessão quando os DOIS gates fecham: `active` (o sinal `nearViewport`+
   * `maxSlideVisto` que o card reaproveita do `<video>` legado) E este elemento estar de fato na
   * viewport. `sessionStarted` trava depois do primeiro disparo — sair e voltar da tela, ou
   * `active`/observer disparando de novo, nunca duplica a sessão.
   */
  private maybeStartSession(): void {
    if (this.sessionStarted || !this.active || !this.inViewport) {
      return;
    }
    this.sessionStarted = true;
    this.startSession();
  }

  private resetForNewVideo(): void {
    this.playbackGeneration++; // invalida qualquer initPlayer() em voo do vídeo anterior (review R2)
    this.pollSub?.unsubscribe();
    this.teardownHls();
    this.uiState = 'processing';
    this.playback = null;
    this.stalled = false;
    this.sessionErrored = false;
    this.playerFailure = false;
    this.renewAttempted = false;
    this.sessionStarted = false;
    this.maybeStartSession();
  }

  // ─── Sessão / polling ───────────────────────────────────────────────────
  private startSession(): void {
    this.uiState = 'processing';
    this.pollSub?.unsubscribe();
    this.pollSub = this.videoPlaybackService.pollSession(this.videoId).subscribe({
      next: (playback) => this.onPlaybackUpdate(playback),
      error: () => this.onSessionError(),
      complete: () => this.onPollComplete(),
    });
  }

  private onPlaybackUpdate(playback: VideoPlayback): void {
    if (playback.schema_version !== SUPPORTED_PLAYBACK_SCHEMA_VERSION) {
      // Contrato: versão de schema desconhecida vira telemetria + fallback visual, nunca
      // tentativa de interpretar um shape que este cliente não conhece. Para o polling também —
      // não faz sentido continuar consultando por 180s um card que já decidimos não saber ler.
      this.reportTelemetry('session', undefined, `unsupported_schema_${playback.schema_version}`);
      this.playback = playback;
      this.uiState = 'failed';
      this.pollSub?.unsubscribe();
      return;
    }

    this.playback = playback;

    if (playback.status === 'READY') {
      this.uiState = 'ready';
      this.startPlaybackIfFresh(playback);
    } else if (playback.status === 'FAILED') {
      this.uiState = 'failed';
    } else {
      this.uiState = 'processing';
    }
  }

  private onSessionError(): void {
    this.sessionErrored = true;
    this.uiState = 'failed';
  }

  private onPollComplete(): void {
    // Só chega aqui sem status terminal quando o polling estourou os 180s do contrato.
    if (this.playback && this.playback.status !== 'READY' && this.playback.status !== 'FAILED') {
      this.stalled = true;
    }
  }

  // ─── Autorização perto de expirar (< 60s) antes de iniciar/retomar ────────
  private isAuthExpiringSoon(playback: VideoPlayback): boolean {
    if (!playback.authorization_expires_at) {
      return false;
    }
    const expiresAt = new Date(playback.authorization_expires_at).getTime();
    if (Number.isNaN(expiresAt)) {
      return false;
    }
    return expiresAt - Date.now() < 60_000;
  }

  private startPlaybackIfFresh(playback: VideoPlayback): void {
    if (!this.isAuthExpiringSoon(playback)) {
      this.initPlayer(playback);
      return;
    }
    this.videoPlaybackService
      .createSession(this.videoId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res.playback.status === 'READY') {
            this.playback = res.playback;
            this.initPlayer(res.playback);
          } else {
            this.failPlayback();
          }
        },
        error: () => this.failPlayback(),
      });
  }

  // ─── Player nativo / hls.js ─────────────────────────────────────────────
  private async initPlayer(playback: VideoPlayback): Promise<void> {
    const video = this.videoElRef?.nativeElement;
    if (!video || !playback.hls_url) {
      return;
    }

    this.teardownHls();

    video.poster = playback.poster_url ?? '';
    video.crossOrigin = 'use-credentials';

    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = playback.hls_url;
      video.load();
      return;
    }

    // Captura ANTES do await: se resetForNewVideo() rodar enquanto o import está em voo, a
    // geração muda e este initPlayer (do vídeo ANTERIOR) reconhece que está obsoleto (review R2).
    const generation = this.playbackGeneration;

    // Import dinâmico: só baixa hls.js quando o navegador não tem HLS nativo (o bundle
    // inicial já está no teto do budget do projeto — ver task-front-brief, ruling #2).
    const { default: HlsCtor } = await import('hls.js');
    if (this.destroyed || generation !== this.playbackGeneration) {
      // O componente morreu, ou o vídeo mudou, enquanto o chunk (1,49 MB) ainda baixava — não
      // cria player órfão/desatualizado apontando pra um hls_url que não é mais o vídeo atual
      // (review I3 e R2).
      return;
    }
    if (!HlsCtor.isSupported()) {
      this.uiState = 'unsupported';
      return;
    }

    const hls = new HlsCtor({
      xhrSetup: (xhr) => {
        xhr.withCredentials = true;
      },
    });
    hls.on(HlsCtor.Events.ERROR, (_event: any, data: ErrorData) => {
      if (data.fatal) {
        this.onHlsFatalError(data);
      }
    });
    hls.loadSource(playback.hls_url);
    hls.attachMedia(video);
    this.hls = hls;
  }

  private teardownHls(): void {
    if (this.hls) {
      this.hls.destroy();
      this.hls = null;
    }
    const video = this.videoElRef?.nativeElement;
    if (video) {
      video.removeAttribute('src');
      video.load();
    }
  }

  private onHlsFatalError(data: ErrorData): void {
    const httpStatus = data.response?.code;
    this.reportTelemetry(this.classifyHlsResource(data.details), httpStatus, data.details);

    if (httpStatus === 404) {
      this.reloadVideoState();
      return;
    }
    if (httpStatus === 401 || httpStatus === 403) {
      this.renewOnce();
      return;
    }
    this.failPlayback();
  }

  private classifyHlsResource(details: string): string {
    if (details.startsWith('manifest')) return 'manifest';
    if (details.startsWith('level')) return 'playlist';
    if (details.startsWith('frag')) return 'segment';
    return 'other';
  }

  /**
   * Contrato: no máximo uma renovação automática por falha — nunca loop. `renewAttempted` só
   * reseta em `onVideoPlaying()` (evidência real de reprodução), NUNCA aqui na resposta da API:
   * a API dizer READY não significa que a reprodução se recuperou — o cenário de falha real
   * (cookie/política do CloudFront rejeitada, key pair errada) pode devolver READY
   * indefinidamente enquanto a mídia continua 401/403 (review B2 — reverter esta linha é
   * exatamente o bug reportado).
   */
  private renewOnce(): void {
    if (this.renewAttempted) {
      this.failPlayback();
      return;
    }
    this.renewAttempted = true;
    this.videoPlaybackService
      .createSession(this.videoId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res.playback.status === 'READY') {
            this.playback = res.playback;
            this.uiState = 'ready';
            this.teardownHls();
            this.initPlayer(res.playback);
          } else {
            this.failPlayback();
          }
        },
        error: () => this.failPlayback(),
      });
  }

  /** 404 do player: o conteúdo pode ter mudado — recarrega o estado do zero (não só a autorização). */
  private reloadVideoState(): void {
    this.teardownHls();
    this.renewAttempted = false;
    this.playerFailure = false;
    this.startSession();
  }

  private failPlayback(): void {
    this.playerFailure = true;
    this.uiState = 'failed';
  }

  /**
   * Telemetria mínima permitida pelo contrato: video_id, status HTTP, tipo do recurso e código
   * do player — nunca cookie/URL assinada. Sentry é o sink real do projeto (main.ts:31-36,
   * ligado ao ErrorHandler em app.module.ts), não `console.warn` (review I1).
   */
  private reportTelemetry(resourceType: string, httpStatus: number | undefined, playerCode: string): void {
    Sentry.captureMessage('hls_playback_error', {
      level: 'warning',
      extra: {
        video_id: this.videoId,
        http_status: httpStatus ?? null,
        resource_type: resourceType,
        player_code: playerCode,
      },
    });
  }
}
